import React, { forwardRef, useEffect, useRef, ReactNode, useState } from 'react';
import { cn } from '../../lib/utils';

export interface PopoverProps {
  children: ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
}

export interface PopoverTriggerProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

export interface PopoverContentProps {
  children: ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
}

interface PopoverContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const PopoverContext = React.createContext<PopoverContextType | null>(null);

function usePopover() {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error('Popover components must be used within a Popover');
  }
  return context;
}

/**
 * Popover root component that manages open/close state
 */
export function Popover({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const triggerElement = triggerRef.current;

      // Check if click is outside both trigger and content
      if (triggerElement && !triggerElement.contains(target)) {
        const popoverContent = document.querySelector('[data-popover-content]');
        if (!popoverContent || !popoverContent.contains(target)) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <PopoverContext.Provider value={{ isOpen, setIsOpen, triggerRef }}>
      <div className="relative">{children}</div>
    </PopoverContext.Provider>
  );
}

/**
 * Popover trigger component
 */
export const PopoverTrigger = forwardRef<HTMLElement, PopoverTriggerProps>(
  ({ children, className, asChild = false }, ref) => {
    const { setIsOpen, triggerRef } = usePopover();

    const handleClick = () => {
      setIsOpen(prev => !prev);
    };

    if (asChild && React.isValidElement(children)) {
      type ChildProps = {
        onClick?: (e: React.MouseEvent<HTMLElement>) => void;
        className?: string;
        ref?: React.Ref<HTMLElement>;
      };

      const child = children as React.ReactElement<ChildProps>;

      return React.cloneElement(child, {
        ref: (node: HTMLElement | null) => {
          triggerRef.current = node;
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else if (ref && 'current' in ref) ref.current = node;
          }
        },
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          if (child.props.onClick) {
            child.props.onClick(e);
          }
          handleClick();
        },
        className: cn(child.props.className, className),
      });
    }

    return (
      <button
        ref={node => {
          triggerRef.current = node;
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else if (ref && 'current' in ref) ref.current = node;
          }
        }}
        onClick={handleClick}
        className={className}
      >
        {children}
      </button>
    );
  }
);

PopoverTrigger.displayName = 'PopoverTrigger';

/**
 * Popover content component with positioning
 */
export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ children, className, align = 'center', side = 'bottom', sideOffset = 4 }, ref) => {
    const { isOpen, triggerRef } = usePopover();
    const contentRef = useRef<HTMLDivElement>(null);

    // Position the popover content
    useEffect(() => {
      if (!isOpen || !triggerRef.current || !contentRef.current) return;

      const trigger = triggerRef.current;
      const content = contentRef.current;
      const triggerRect = trigger.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top: number;
      let left: number;

      // Calculate vertical position
      switch (side) {
        case 'top':
          top = triggerRect.top - contentRect.height - sideOffset;
          break;
        case 'bottom':
          top = triggerRect.bottom + sideOffset;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height - contentRect.height) / 2;
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height - contentRect.height) / 2;
          break;
      }

      // Calculate horizontal position
      switch (side) {
        case 'top':
        case 'bottom':
          switch (align) {
            case 'start':
              left = triggerRect.left;
              break;
            case 'center':
              left = triggerRect.left + (triggerRect.width - contentRect.width) / 2;
              break;
            case 'end':
              left = triggerRect.right - contentRect.width;
              break;
          }
          break;
        case 'left':
          left = triggerRect.left - contentRect.width - sideOffset;
          break;
        case 'right':
          left = triggerRect.right + sideOffset;
          break;
      }

      // Adjust for viewport boundaries
      if (left < 8) left = 8;
      if (left + contentRect.width > viewportWidth - 8) {
        left = viewportWidth - contentRect.width - 8;
      }
      if (top < 8) top = 8;
      if (top + contentRect.height > viewportHeight - 8) {
        top = Math.max(8, triggerRect.top - contentRect.height - sideOffset);
      }

      content.style.position = 'fixed';
      content.style.top = `${top}px`;
      content.style.left = `${left}px`;
      content.style.zIndex = '50';
    }, [isOpen, align, side, sideOffset, triggerRef]); // Include triggerRef in dependencies

    if (!isOpen) return null;

    return (
      <div
        ref={node => {
          contentRef.current = node;
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else ref.current = node;
          }
        }}
        data-popover-content
        className={cn(
          'min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
      >
        {children}
      </div>
    );
  }
);

PopoverContent.displayName = 'PopoverContent';
